import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { CallProvider } from '@/modules/comms/call/CallProvider';
import { GlobalCallBar } from '@/modules/comms/call/GlobalCallBar';
import { CommsProvider } from '@/modules/comms/providers/CommsProvider';
import {
  ConversationDrawerContext,
  type ConversationTarget,
} from '@/modules/comms/context/ConversationDrawerContext';
import { ChatDrawer } from '@/modules/comms/components/ChatDrawer';
import { MinimizedConversationButton } from '@/modules/comms/components/MinimizedConversationButton';
import { AuthenticatedCommsStartup } from './AuthenticatedCommsStartup';
import { useIdTokenClaims } from '@/hooks';

interface IdentityScopedCommsShellProps extends PropsWithChildren {
  callerSubject: string | null;
}

const IdentityScopedCommsShell = ({
  callerSubject,
  children,
}: IdentityScopedCommsShellProps) => {
  const [target, setTarget] = useState<ConversationTarget | null>(null);
  const [drawerState, setDrawerState] = useState<
    'closed' | 'open' | 'minimized'
  >('closed');
  const closeConversation = useCallback(() => setDrawerState('closed'), []);
  const minimizeConversation = useCallback(
    () => setDrawerState('minimized'),
    [],
  );
  const restoreConversation = useCallback(() => {
    if (target) setDrawerState('open');
  }, [target]);
  const openConversation = useCallback((nextTarget: ConversationTarget) => {
    setTarget(nextTarget);
    setDrawerState('open');
  }, []);
  const returnToConversation = useCallback(
    (conversationOperatorId: number) =>
      openConversation({ userId: conversationOperatorId }),
    [openConversation],
  );
  const conversationDrawer = useMemo(
    () => ({
      target,
      isOpen: drawerState === 'open',
      openConversation,
      minimizeConversation,
      restoreConversation,
      closeConversation,
    }),
    [
      closeConversation,
      drawerState,
      minimizeConversation,
      openConversation,
      restoreConversation,
      target,
    ],
  );
  const handleDrawerVisibilityChange = useCallback(
    (visible: boolean) => {
      if (!visible && drawerState === 'closed') setTarget(null);
    },
    [drawerState],
  );

  return (
    <CommsProvider callerSubject={callerSubject}>
      <AuthenticatedCommsStartup callerSubject={callerSubject} />
      <ConversationDrawerContext.Provider value={conversationDrawer}>
        <CallProvider onReturnToConversation={returnToConversation}>
          <GlobalCallBar />
          {children}
          {target && drawerState === 'minimized' && (
            <MinimizedConversationButton
              operator={target}
              onClick={restoreConversation}
            />
          )}
          <ChatDrawer
            operator={target ?? undefined}
            open={drawerState === 'open'}
            onMinimize={minimizeConversation}
            onClose={closeConversation}
            onVisibilityChange={handleDrawerVisibilityChange}
          />
        </CallProvider>
      </ConversationDrawerContext.Provider>
    </CommsProvider>
  );
};

export const CommsAppShell = ({ children }: PropsWithChildren) => {
  const { hasLoggedIn, idTokenClaims } = useIdTokenClaims();
  const callerSubject = hasLoggedIn ? (idTokenClaims?.sub ?? null) : null;

  return (
    <IdentityScopedCommsShell
      key={callerSubject ?? 'unauthenticated'}
      callerSubject={callerSubject}
    >
      {children}
    </IdentityScopedCommsShell>
  );
};
